// Receipt file handling: the standard filename, format normalization before
// upload, and the display helpers for a receipt already on an expense.
//
// The vault stores every file as `<uuid>.<ext>` and always will — the human
// name lives in `documents.original_filename`, which is what the app renders and
// what the browser saves on download. So "name receipts consistently" is
// entirely a matter of what we put in the X-Filename header.

import { epochToDateInput } from './date';

/** What the browser can hand to the API for server-side PDF conversion. */
const SERVER_CONVERTIBLE = new Set(['image/jpeg', 'image/jpg', 'image/png']);

/**
 * One segment of the filename: spaces and separators become single dashes,
 * everything outside a conservative allowlist is dropped. Returns '' for input
 * with nothing usable in it, so the caller can leave the segment out entirely
 * rather than emit a stray delimiter.
 */
function segment(raw: string | null | undefined, max = 40): string {
  return (raw ?? '')
    .trim()
    .replace(/[\s/\\]+/g, '-')
    .replace(/[^A-Za-z0-9.\-&]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, max)
    .replace(/[-.]+$/, '');   // a mid-word cut can leave a trailing dash
}

export interface ReceiptFilenameInput {
  /** Project case number, or null for a general-business expense. */
  caseNumber: string | null;
  dateEpoch: number | null;
  /** The receipt's own number, off the receipt or typed. The third segment. */
  receiptNumber: string | null;
  /** Picked vendor's name, a generic category, or a typed one-off — the first
   *  fallback when the receipt carries no number. */
  vendor: string | null;
  /** Expense type — the last fallback before "Receipt". */
  type: string;
  /** Extension of the file being uploaded, with or without the dot. */
  ext: string;
}

/**
 * `SFS-2026-0001_2026-08-06_004821A.jpg`
 *
 * Project, then date, then the receipt's own number. Case first so a folder of
 * loose receipts groups by matter; a general-business expense uses "General" in
 * its place.
 *
 * **The third segment falls back** — vendor, then expense type, then the literal
 * "Receipt". Most receipts carry no readable number, and without a fallback
 * every receipt on one project on one day would be named identically. Nothing
 * collides on disk (the vault stores UUIDs), but two identical
 * `original_filename`s in the Documents list can't be told apart.
 *
 * The server rewrites the extension to `.pdf` when it converts the image, so
 * pass the real extension of what's being uploaded.
 */
export function buildReceiptFilename(input: ReceiptFilenameInput): string {
  const parts = [
    segment(input.caseNumber, 30) || 'General',
    epochToDateInput(input.dateEpoch),
    segment(input.receiptNumber, 32)
      || segment(input.vendor)
      || segment(input.type)
      || 'Receipt',
  ].filter((p) => p.length > 0);

  const ext = input.ext.replace(/^\.+/, '').toLowerCase().replace(/[^a-z0-9]/g, '');
  // 120 leaves room for the ".pdf" the server may swap in and keeps the whole
  // thing well inside every filesystem's per-name limit.
  const stem = parts.join('_').slice(0, 120).replace(/[-._]+$/, '');
  return ext.length > 0 ? `${stem}.${ext}` : stem;
}

/** The extension of a File, without the dot (''), lowercased. */
export function extensionOf(file: File): string {
  const m = /\.([A-Za-z0-9]+)$/.exec(file.name);
  return m ? m[1]!.toLowerCase() : '';
}

/**
 * Re-encode an image the server can't convert into one it can.
 *
 * The API wraps receipts in a PDF with pdfkit, which embeds JPEG and PNG and
 * nothing else. The browser, though, can decode anything it can display — webp,
 * gif, bmp — so drawing it to a canvas and reading back JPEG widens the set at
 * no cost to the API image. HEIC is the gap that stays: Safari decodes it,
 * Chrome doesn't, and nothing here can change that. When decoding fails for any
 * reason the original file is returned untouched and uploads as-is; a receipt
 * stored in an awkward format beats a receipt that didn't upload.
 *
 * PDFs and already-convertible images pass straight through.
 */
export async function normalizeReceiptFile(file: File): Promise<File> {
  const type = file.type.toLowerCase();
  if (type === 'application/pdf' || SERVER_CONVERTIBLE.has(type)) return file;
  if (!type.startsWith('image/')) return file;

  try {
    const bitmap = await createImageBitmap(file);
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
    if (!blob) return file;
    return new File([blob], file.name.replace(/\.[^.]*$/, '') + '.jpg', { type: 'image/jpeg' });
  } catch {
    return file;
  }
}

/** Rename a file without touching its bytes or type. */
export function renameFile(file: File, name: string): File {
  return new File([file], name, { type: file.type, lastModified: file.lastModified });
}

/**
 * The name a receipt WILL be stored under, for showing in the form before it
 * uploads. Same as `buildReceiptFilename` except the extension is `.pdf`
 * whenever the file is one the pipeline converts — so the field never promises
 * a conversion that isn't going to happen (a `.heic` Chrome can't decode keeps
 * its own extension).
 */
export function previewReceiptFilename(
  file: File,
  input: Omit<ReceiptFilenameInput, 'ext'>,
): string {
  const type = file.type.toLowerCase();
  const willConvert = type === 'application/pdf'
    || SERVER_CONVERTIBLE.has(type)
    // Anything else the browser can draw gets re-encoded to JPEG first, and
    // JPEG converts. `createImageBitmap` is the real test and it's async, so
    // this is the same optimistic guess normalizeReceiptFile acts on.
    || type.startsWith('image/');
  return buildReceiptFilename({ ...input, ext: willConvert ? 'pdf' : extensionOf(file) });
}

// ---------- Displaying a receipt already on an expense ----------
//
// `receipt_filename` / `receipt_mime_type` come from the joined `documents` row
// (see RECEIPT_JOIN_SQL in routes/expenses.ts). They're null for legacy rows
// whose `receipt_path` has no live documents row.

export interface ReceiptBearing {
  id: number;
  receipt_path?: string | null;
  receipt_filename?: string | null;
  receipt_mime_type?: string | null;
}

const MIME_BY_EXT: Record<string, string> = {
  pdf: 'application/pdf',
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  heic: 'image/heic',
};

export function receiptMime(e: ReceiptBearing): string {
  if (e.receipt_mime_type) return e.receipt_mime_type;
  const name = (e.receipt_filename ?? e.receipt_path ?? '').toLowerCase();
  const ext = /\.([a-z0-9]+)$/.exec(name)?.[1] ?? '';
  return MIME_BY_EXT[ext] ?? 'application/octet-stream';
}

/**
 * What to show as the receipt's name. The joined `original_filename` is the
 * human one and wins.
 *
 * The fallback deliberately does NOT use the stored path's basename: that is the
 * vault UUID, which tells the user nothing and looks like a bug. Keep the
 * extension from the path — it's the one useful thing in it.
 */
export function receiptFilename(e: ReceiptBearing): string {
  if (e.receipt_filename && e.receipt_filename.trim().length > 0) return e.receipt_filename;
  const ext = /\.([a-z0-9]+)$/i.exec(e.receipt_path ?? '')?.[1];
  return ext ? `Receipt.${ext.toLowerCase()}` : 'Receipt';
}
